import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { NavPreview } from "@/components/admin/NavPreview";
import { Save, Loader2, Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function NavPanel() {
  const qc = useQueryClient();
  const { data, refetch } = useQuery({ queryKey: ["admin-nav"], queryFn: () => api.get("/admin/nav-config").then((r) => r.data) });
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (data) setItems(data.map((d) => ({ ...d }))); }, [data]);

  const upd = (i, k, v) => setItems((p) => p.map((it, j) => j === i ? { ...it, [k]: v } : it));
  const move = (i, dir) => setItems((p) => {
    const n = [...p]; const j = i + dir;
    if (j < 0 || j >= n.length) return p;
    [n[i], n[j]] = [n[j], n[i]];
    return n.map((it, k) => ({ ...it, order: k + 1 }));
  });
  const addItem = () => setItems((p) => [...p, { key: `custom-${Date.now()}`, label: "New link", to: "/", group: "explore", visible: true, order: p.length + 1, custom: true }]);
  const remove = (i) => setItems((p) => p.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/nav-config", { items: items.map((it, k) => ({ ...it, order: k + 1 })) });
      toast.success("Menu saved — it updates live");
      refetch();
      qc.invalidateQueries({ queryKey: ["nav-config"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset the menu to defaults?")) return;
    try { await api.post("/admin/nav-config/reset"); toast.success("Menu reset"); refetch(); qc.invalidateQueries({ queryKey: ["nav-config"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-nav-panel">
      <div className="flex items-center justify-between mb-2">
        <div><h2 className="font-serif text-2xl text-[#1A242B]">Menu editor</h2><p className="text-sm text-[#4A5D6B]">Rename, reorder, hide, or add top-menu links without touching code. "Primary" links show in the top bar; "Explore" links go in the Explore dropdown.</p></div>
        <div className="flex gap-2">
          <button onClick={reset} data-testid="nav-reset-btn" className="eyw-btn-ghost !py-2 text-sm"><RotateCcw size={15} /> Reset</button>
          <button onClick={save} disabled={saving} data-testid="nav-save-btn" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save menu</button>
        </div>
      </div>

      <div className="space-y-2 mt-5">
        {items.map((it, i) => (
          <div key={i} className={`eyw-card !p-3 flex flex-wrap items-center gap-2 ${!it.visible ? "opacity-60" : ""}`} data-testid={`nav-item-${it.key}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} className="text-[#8FA89B] hover:text-[#144F75]"><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} className="text-[#8FA89B] hover:text-[#144F75]"><ArrowDown size={14} /></button>
            </div>
            <input value={it.label} onChange={(e) => upd(i, "label", e.target.value)} data-testid={`nav-label-${it.key}`} className="eyw-input !py-1.5 text-sm w-40" placeholder="Label" />
            <input value={it.to} onChange={(e) => upd(i, "to", e.target.value)} className="eyw-input !py-1.5 text-sm w-44 font-mono text-xs" placeholder="/path" />
            <select value={it.group} onChange={(e) => upd(i, "group", e.target.value)} data-testid={`nav-group-${it.key}`} className="eyw-input !py-1.5 text-sm w-auto">
              <option value="primary">Top bar</option>
              <option value="explore">Explore ▾</option>
            </select>
            <button onClick={() => upd(i, "visible", !it.visible)} data-testid={`nav-visible-${it.key}`} title={it.visible ? "Visible" : "Hidden"} className="text-[#144F75]">{it.visible ? <Eye size={17} /> : <EyeOff size={17} />}</button>
            <button onClick={() => remove(i)} className="text-red-500 ml-auto"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <button onClick={addItem} data-testid="nav-add-item" className="eyw-btn-ghost !py-2 text-sm mt-3"><Plus size={15} /> Add link</button>
      <NavPreview items={items} />
    </div>
  );
}
